/**
 * "Hur säker är du?" — the one-tap confidence rating shown after the answer
 * input and before the submit button (pedagogy spec §4.3.2, B6; §13 Q1, Q5).
 * Stored as 1–4 (D5); the labels are the core keys `common.confidence.*`,
 * shared with Progress. Required in the UI: the page refuses a submit without
 * it, shows `error` and calls `focus()`.
 *
 * Keyboard (ruling 4, slice C): with focus in the group, 1–4 picks a level and
 * Enter (or S) submits, so "type the answer, Enter, 3, Enter" works without the
 * mouse. The answer field names this in its `aria-describedby` hint.
 */
import { forwardRef, useImperativeHandle, useRef, type KeyboardEvent } from "react";
import type { ConfidenceLevel } from "@shared/attemptAnswer";
import { RadioGroup } from "@client/components/ui";
import { useT } from "@client/i18n";
import { answerKeyDown } from "./answerKeys";

export const CONFIDENCE_LEVELS: readonly ConfidenceLevel[] = [1, 2, 3, 4];

/** The level a digit key picks, if any. */
export function confidenceForKey(key: string): ConfidenceLevel | undefined {
  return CONFIDENCE_LEVELS.find((level) => String(level) === key);
}

export interface ConfidenceControlHandle {
  /** Moves focus into the group: the checked level, else the first. */
  focus(): void;
}

export interface ConfidenceControlProps {
  value: ConfidenceLevel | undefined;
  onChange: (level: ConfidenceLevel) => void;
  /** Inline error, set by the page when a submit was refused for a missing rating. */
  error?: string;
  /** The step's submit handler ("Svara", or "Klar" on an open question). */
  onSubmit: () => void;
  /** Opens the next hint (the H shortcut). */
  onRevealHint?: (() => void) | undefined;
  /** A legend of its own, when more than one rating sits on the page (one per part, B4). */
  legend?: string | undefined;
}

export const ConfidenceControl = forwardRef<ConfidenceControlHandle, ConfidenceControlProps>(
  function ConfidenceControl({ value, onChange, error, onSubmit, onRevealHint, legend }, ref) {
    const t = useT();
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      ref,
      () => ({
        focus() {
          const radios =
            containerRef.current?.querySelectorAll<HTMLInputElement>('input[type="radio"]');
          if (!radios || radios.length === 0) return;
          const checked = Array.from(radios).find((radio) => radio.checked);
          (checked ?? radios[0]).focus();
        },
      }),
      [],
    );

    const submitKeys = answerKeyDown({ submit: onSubmit, hint: onRevealHint });
    function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
      const level = confidenceForKey(event.key);
      const target = event.target;
      if (
        level !== undefined &&
        !event.repeat &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        target instanceof HTMLInputElement &&
        target.type === "radio"
      ) {
        event.preventDefault();
        onChange(level);
        containerRef.current
          ?.querySelector<HTMLInputElement>(`input[type="radio"][value="${level}"]`)
          ?.focus();
        return;
      }
      submitKeys(event);
    }

    return (
      // Key events bubble from the radios; `RadioGroup` takes no handler of its own.
      <div ref={containerRef} onKeyDown={onKeyDown}>
        <RadioGroup
          legend={legend ?? t("common.confidence.question")}
          value={value === undefined ? null : String(value)}
          onChange={(next) => onChange(Number(next) as ConfidenceLevel)}
          options={CONFIDENCE_LEVELS.map((level) => ({
            value: String(level),
            label: t(`common.confidence.${level}`),
            // Ruling 4: digits 1–4 pick the level while focus is in the group (review M6).
            keyShortcuts: String(level),
          }))}
          className="!flex-row flex-wrap gap-x-5 gap-y-2 max-sm:gap-x-2 max-sm:gap-y-2"
          // Pill-style options on phones (UX-24): the whole pill, radio and label, is one
          // >= 44 px tap target, like the exam wrapper's CausesCard.
          optionClassName="max-sm:min-h-11 max-sm:items-center max-sm:gap-0 max-sm:rounded-lg max-sm:border max-sm:border-ink-300 max-sm:[&>input]:ml-3 dark:max-sm:border-ink-700"
          labelClassName="max-sm:flex max-sm:min-h-11 max-sm:items-center max-sm:pl-2 max-sm:pr-3"
        />
        {error && (
          <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">
            {error}
          </p>
        )}
      </div>
    );
  },
);
